import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Select } from "antd";
import {
  Button,
  Collapse,
  Form,
  Input,
  InputNumber,
  Space,
  Switch,
  Tag,
} from "antd";
import React, { useState } from "react";
import { GroupFormSelect } from "~/components/form/GroupFormSelect.jsx";
import { SelectTagWithAddNew } from "~/components/form/SelectTagWithAddNew.jsx";
import { HelpLine } from "~/components/help/helpLine.jsx";
import { $t } from "~/i18n.js";
import { OrderDeleteButtons } from "~/pages/admin/dto/components/OrderDeleteButtons.jsx";


export const RegistrationListTemplate = ({ value = [], onChange }) => {
  const [activeKey, setActiveKey] = useState([]);

  // get unique clusters to set as category option
  const clusters =
    value && value.length > 0
      ? value.map((d) => d.cluster).filter((v, i, a) => v && a.indexOf(v) === i)
      : [];

  const handleAdd = () => {
    const newList = {
      id: `new_${value.length}`,
      name: $t("Nieuwe lijst"),
      order: value.length + 1,
      groups: [],
      cluster: "",
      has_waitinglist: true,
      automatic_assignment: true,
      registration_list_length: 2,
      list_length_type: "fixed",
      max_registrations_allowed: 2,
      per_instructor: false,
    };
    onChange([...value, newList]);
    setActiveKey([value.length.toString()]);
  };

  const handleDelete = (index) => {
    const newValue = value.map((d, i) => {
      if (i === index) return { ...d, delete: !d.delete };
      return d;
    });
    onChange(newValue);
  };

  const handleUpdate = (index, field, fieldValue) => {
    const newValue = [...value];
    newValue[index] = { ...newValue[index], [field]: fieldValue };
    onChange(newValue);
  };

  const handleOrderChange = (newOrder) => {
    onChange(newOrder);
  };

  return (
    <div>
      <HelpLine>
        {$t(
          "Registratielijsten zijn geschikt wanneer gebruikers zich zelf kunnen inschrijven, waarbij een maximaal aantal inschrijvingen per lijst met een wachtlijst gewenst is, zoals dbo'ers voor een lesdag."
        )}
      </HelpLine>

      <Collapse
        activeKey={activeKey}
        onChange={setActiveKey}
        style={{ marginTop: 12 }}
        items={value
          .sort((a, b) => a.order - b.order)
          .map((list, index) => ({
            key: index.toString(),
            label: (
              <p>
                {list.name} {list.cluster && <Tag>{list.cluster}</Tag>}
              </p>
            ),
            extra: (
                <div className="row" style={{ gap: 6 }}>
                  {list.delete && <Tag color="red">{$t("Verwijderen")}</Tag>}

                  <Button
                    key="2"
                    danger
                    shape="circle"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(index);
                    }}
                    icon={<DeleteOutlined />}
                  />
                  <OrderDeleteButtons
                    key="order"
                    loading={false}
                    task_index={index}
                    task={list}
                    tasks={value}
                    saveTasks={handleOrderChange}
                    hasDelete={false}
                  />
                </div>
              ),
            children: (
              <Form
                layout="horizontal"
                size="small"
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
              >
                <Form.Item label={$t("Naam")}>
                  <Input
                    value={list.name}
                    onChange={(e) =>
                      handleUpdate(index, "name", e.target.value)
                    }
                    placeholder={$t("Naam van de lijst")}
                  />
                </Form.Item>

                <Form.Item
                  label={$t("Cluster")}
                  extra={$t(
                    "Optioneel, gebruikers kunnen maximaal één keer inschrijven per cluster."
                  )}
                >
                  <SelectTagWithAddNew
                    allTags={clusters}
                    value={list.cluster}
                    onChange={(value) => handleUpdate(index, "cluster", value)}
                  />
                </Form.Item>

                <Form.Item
                  label={$t("Groepen")}
                  extra={$t(
                    "Optioneel, limiteer de groepen die zich op deze lijst kunnen inschrijven"
                  )}
                >
                  <GroupFormSelect
                    value={list.groups}
                    onChange={(val) => handleUpdate(index, "groups", val)}
                  />
                </Form.Item>

                <Form.Item
                  label={$t("Lijst lengte")}
                  extra={$t("Maximaal aantal inschrijvingen op de lijst")}
                >
                  <InputNumber
                    value={list.registration_list_length}
                    onChange={(val) =>
                      handleUpdate(index, "registration_list_length", val)
                    }
                    min={0}
                    style={{ width: "100%" }}
                  />
                </Form.Item>

                <Form.Item
                  label={$t("Type lijst lengte")}
                  extra={$t(
                    "Kies of de lijst lengte vast is of per aangemelde instructeur wordt bepaald"
                  )}
                >
                  <Select
                    value={list.list_length_type || "fixed"}
                    onChange={(v) => handleUpdate(index, "list_length_type", v)}
                  >
                    <Select.Option value="fixed">{$t("Vast")}</Select.Option>
                    <Select.Option value="per_instructor">
                      {$t("Per instructeur")}
                    </Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  label={$t("Max toekomst inschrijvingen")}
                  extra={$t(
                    "Maximaal aantal inschrijvingen per gebruiker die in de toekomst toegestaan is (per cluster), om te zorgen dat er voldoende ruimte is voor andere gebruikers."
                  )}
                >
                  <InputNumber
                    value={list.max_registrations_allowed}
                    onChange={(val) =>
                      handleUpdate(index, "max_registrations_allowed", val)
                    }
                    min={1}
                    style={{ width: "100%" }}
                  />
                </Form.Item>

                <Space direction="vertical" style={{ width: "100%" }}>
                  <Form.Item
                    label={$t("Wachtlijst")}
                    style={{ marginBottom: 8 }}
                    extra={$t(
                      "Gebruikers kunnen op de wachtlijst als er geen ruimte meer is op de lijst"
                    )}
                  >
                    <Switch
                      checked={list.has_waitinglist}
                      onChange={(checked) =>
                        handleUpdate(index, "has_waitinglist", checked)
                      }
                      checkedChildren={$t("Ja")}
                      unCheckedChildren={$t("Nee")}
                    />
                  </Form.Item>

                  <Form.Item
                    label={$t("Automatische toewijzing")}
                    style={{ marginBottom: 0 }}
                    extra={$t(
                      "Gebruikers kunnen automatisch van de wachtlijst naar de lijst verplaatst zodra er ruimte beschikbaar komt, op basis van laatst gevlogen datum."
                    )}
                  >
                    <Switch
                      checked={list.automatic_assignment}
                      onChange={(checked) =>
                        handleUpdate(index, "automatic_assignment", checked)
                      }
                      checkedChildren={$t("Ja")}
                      unCheckedChildren={$t("Nee")}
                    />
                  </Form.Item>
                </Space>
              </Form>
            ),
          }))}
      />

      {value.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>
          {$t("Geen registratie lijsten")}
        </div>
      )}

      <Button
        style={{ marginTop: 12 }}
        type="dashed"
        icon={<PlusOutlined />}
        onClick={handleAdd}
      >
        {$t("Registratie lijst toevoegen")}
      </Button>
    </div>
  );
};
